import { Injectable } from '@jack-henry/frontend-utils/di';
import { SecurityQuestionModelDto, UserModelDto } from '@treasury/api/channel';
import { AccountService } from '@treasury/domain/channel/services';
import { SecureTokenActionType } from '@treasury/domain/channel/services/account';
import TermsAndConditionsService from '@treasury/domain/channel/services/account/terms-and-conditions-service';
import { AuthenticationService } from '@treasury/domain/services/authentication';

@Injectable()
export class LoginClient {
    constructor(
        private readonly authService: AuthenticationService,
        private readonly accountService: AccountService
    ) {}

    public login(credentials: {
        institution: string;
        companyId: string;
        loginId: string;
        password: string;
    }) {
        return this.accountService.login(credentials);
    }

    public async checkLoginCompletion() {
        const response = await this.accountService.checkLoginCompletion();
        const { isComplete } = response;

        if (isComplete) {
            this.authService.authenticate();
        }

        return response;
    }

    public saveSecurityQuestions(securityQuestions: SecurityQuestionModelDto[]) {
        return this.accountService.saveSecurityQuestions(securityQuestions);
    }

    public verifySecurityQuestions(securityQuestionsPayload: SecurityQuestionModelDto[]) {
        return this.accountService.verifySecurityQuestions(securityQuestionsPayload);
    }

    public registerSecureToken(
        actionType: SecureTokenActionType,
        credentialId?: string,
        token?: string,
        pin?: string
    ) {
        return this.accountService.registerSecureToken(credentialId, token, pin, actionType);
    }

    public forgotPassword(user: UserModelDto) {
        return this.accountService.forgotPassword(user);
    }

    public async sendOneTimePassword(phone: string, method: string, useForBoth: boolean) {
        return this.accountService.sendOneTimePassword(phone, method, useForBoth);
    }

    public async validateOneTimePassword(oneTimePassword: string) {
        return this.accountService.validateOneTimePassword(oneTimePassword);
    }

    public async setOutOfBandPreferredMethod(method: string) {
        return this.accountService.sendOutOfBandPreferredMethod(method);
    }

    public async oobRemindMeLater() {
        return this.accountService.oobRemindMeLater();
    }

    public verifyUser(user: UserModelDto) {
        return this.accountService.verifyUser(user);
    }

    public passwordRequirements(institution: string) {
        return this.accountService.getPasswordRequirements(institution);
    }

    public updatePassword(user: UserModelDto) {
        return this.accountService.updatePassword(user);
    }

    public fetchOutOfBandConfig() {
        return this.accountService.getSecurityConfig();
    }

    public async removeVerificationMethod(methodType: string) {
        return this.accountService.removeVerificationMethod(methodType);
    }

    public completeOutOfBandConfig() {
        return this.accountService.completeOutOfBandConfiguration();
    }

    public getTermsAndConditions() {
        return TermsAndConditionsService.getTermsAndConditions();
    }

    public checkTermsAndConditions() {
        return TermsAndConditionsService.checkTermsAndConditions();
    }
}
